import { Route } from "@angular/router";
import { initialDataResolver } from "app/app.resolvers";
import { AuthGuard } from "app/core/auth/guards/auth.guard";
import { NoAuthGuard } from "app/core/auth/guards/noAuth.guard";
import { LayoutComponent } from "app/layout/layout.component";
import { PermissionGuard } from "./core/auth/guards/permission.guard";
import { DashboardComponent } from "./modules/admin/dashboard/dashboard.component";
import { RoleComponent } from "./modules/admin/role/role.component";
import { UserComponent } from "./modules/admin/user/user.component";
import { NotFoundComponent } from "./modules/common/not-found/not-found.component";
import { MaintenanceComponent } from "./modules/common/maintenance/maintenance.component";
import { ServerErrorComponent } from "./modules/common/server-error/server-error.component";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: "", pathMatch: "full", redirectTo: "dashboard" },

  // Redirect signed-in user to the '/example'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: "signed-in-redirect", pathMatch: "full", redirectTo: "dashboard" },

  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "confirmation-required",
        loadChildren: () =>
          import(
            "app/modules/auth/confirmation-required/confirmation-required.routes"
          ),
      },
      {
        path: "forgot-password",
        loadChildren: () =>
          import("app/modules/auth/forgot-password/forgot-password.routes"),
      },
      {
        path: "reset-password",
        loadChildren: () =>
          import("app/modules/auth/reset-password/reset-password.routes"),
      },
      {
        path: "sign-in",
        loadChildren: () => import("app/modules/auth/sign-in/sign-in.routes"),
        data: { title: "One Vision | Third Party Risk Management" },
      },
      {
        path: "sign-up",
        loadChildren: () => import("app/modules/auth/sign-up/sign-up.routes"),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "sign-out",
        loadChildren: () => import("app/modules/auth/sign-out/sign-out.routes"),
      },
      {
        path: "unlock-session",
        loadChildren: () =>
          import("app/modules/auth/unlock-session/unlock-session.routes"),
      },
    ],
  },

  // Landing routes
  {
    path: "",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "home",
        loadChildren: () => import("app/modules/landing/home/home.routes"),
      },
    ],
  },

  // Admin routes
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    data: { breadcrumb: "Home" },
    children: [
      {
        path: "dashboard",
        component: DashboardComponent,
        data: {
          breadcrumb: "dashboard",
          permission: "Dashboard",
          title: "Dashboard",
        },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
      },
      {
        path: "role",
        component: RoleComponent,
        data: {
          breadcrumb: "Role",
          permission: "Role_Management",
          title: "Role Management",
        },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
      },
      {
        path: "user",
        component: UserComponent,
        data: {
          breadcrumb: "User",
          permission: "User_Management",
          title: "User Management",
        },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
      },
      {
        path: "question-bank",
        loadChildren: () =>
          import("app/modules/admin/question-bank/question-bank.route"),
        data: {
          breadcrumb: "question-bank",
          permission: "Question_Bank",
          title: "Question Bank",
        },
      },
      {
        path: "contract",
        loadChildren: () => import("app/modules/admin/contract/contract.route"),
        data: {
          breadcrumb: "contract",
          permission: "Contract",
          title: "Contract",
        },
      },
      {
        path: "rtt-id-render",
        loadChildren: () =>
          import(
            "app/modules/customer/vendor-inventory/vendor-inventory.route"
          ),
        data: {
          breadcrumb: "rtt-id-render",
          permission: "Rtt_Id_Render",
          title: "Rtt Id Render",
        },
      },
      {
        path: "workflow",
        loadChildren: () =>
          import("app/modules/admin/workflow/workflow.routes"),
        data: {
          breadcrumb: "Workflow",
          permission: "Workflow",
          title: "Workflow",
        },
      },
      {
        path: "offboarding",
        loadChildren: () =>
          import("app/modules/offboarding/offboarding.routes"),
        data: {
          breadcrumb: "offboarding",
          // permission: "Workflow",
          title: "offboarding",
        },
      },
      {
        path: "setting",
        loadChildren: () => import("./modules/admin/settings/settings.routes"),
      },
    ],
  },
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    data: { breadcrumb: "Home" },
    children: [
      {
        path: "vendor-inventory",
        loadChildren: () =>
          import(
            "app/modules/customer/vendor-inventory/vendor-inventory.route"
          ),
        data: {
          breadcrumb: "vendor-inventory",
          permission: "Vendor Inventory",
          title: "Vendor Inventory",
        },
      },

      {
        path: "risk-tiering",
        loadChildren: () =>
          import("app/modules/customer/risk-tiering/risk-tiering.route"),
        data: {
          breadcrumb: "risk-tiering",
          permission: "Risk Tiering",
          title: "Risk Tiering",
        },
      },
      {
        path: "assessment",
        loadChildren: () =>
          import("app/modules/customer/assessment/assessment.route"),
        data: {
          breadcrumb: "assessment",
          permission: "Assessment",
          title: "Assessment",
        },
      },
      {
        path: "Offboarding",
        loadChildren: () =>
          import("app/modules/customer/assessment/assessment.route"),
        data: {
          breadcrumb: "Offboarding",
          permission: "Offboarding",
          title: "Offboarding",
        },
      },
      {
        path: "re-assessment-tracker",
        loadChildren: () =>
          import(
            "app/modules/admin/re-assessment-tracker/re-assessment-tracker.routes"
          ).then((m) => m.reAssessmentTrackerRoutes),
        data: {
          breadcrumb: "Re-Assessment Tracker",
          permission: "Reassessment",
          title: "Re-Assessment Tracker",
        },
        canActivate: [PermissionGuard],
        canActivateChild: [PermissionGuard],
      },
      {
        path: "risk-summary",
        loadChildren: () =>
          import("app/modules/customer/risk-management/risk-management.route"),
        data: {
          breadcrumb: "risk-management",
          permission: "Risk Management",
          title: "Risk Management",
        },
      },
      {
        path: "business-risk-dashboard",
        loadChildren: () =>
          import(
            "app/modules/admin/business-risk-dashboard/business-risk-dashboard.route"
          ),
        data: {
          breadcrumb: "business-risk-dashboard",
          permission: "business-risk-dashboard",
          title: "Business Risk",
        },
      },
      {
        path: "business-risk",
        loadChildren: () =>
          import("app/modules/admin/business-risk/business-risk.route"),
        data: {
          breadcrumb: "business-risk",
          permission: "business-risk",
          title: "Business Risk",
        },
      },
      {
        path: "my-workspace",
        loadChildren: () =>
          import("app/modules/customer/my-workspace/my-workspace.route"),
        data: {
          breadcrumb: "my-workspace",
          permission: "My Workspace",
          title: "My Workspace",
        },
      },
      {
        path: "evaluation-parameter",
        loadChildren: () =>
          import(
            "app/modules/admin/question-bank/evaluation-parameter/evaluation-parameter.route"
          ),
        data: {
          breadcrumb: "evaluation-parameter",
          permission: "Evaluation Parameter",
          title: "Evaluation Parameter",
        },
      },
    ],
  },
  { path: "maintenance", component: MaintenanceComponent },
  { path: "server-error", component: ServerErrorComponent },
  { path: "**", component: NotFoundComponent },
  
];

